<template>
    <v-container class="pa-5" fluid>
        <CodeReaderDialog
            :is-open="codeReaderDialog"
            :settings="codeReaderSettings"
            @code-stream-start="showCodeReaderDialog"
            @code-stream-stop="hideCodeReaderDialog"
            @code-stream-handle="handleQrJSON"
        />

        <TaskDialog
            :is-open="taskDialog"
            :qr-code="decodedQr"
            :has-camera="hasCamera"
            :action-loading="actionLoading"
            @code-stream-start="showCodeReaderDialog"
            @on-confirm="setTasksClipboard"
            @on-close="closeTaskDialog"
        />

        <LocalizationDialog
            :is-open="localizationDialog"
            :qr-code="decodedQr"
            :localizations="localizations"
            :tasks="tasksSelectedToDrop"
            :has-camera="hasCamera"
            @code-stream-start="showCodeReaderDialog"
            @on-confirm="removeFromClipboard"
            @on-close="closeLocalizationDialog"
        />

        <page-title>Tracking</page-title>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="secondary"
                                medium
                                data-sentry-label="Dodaj do schowka"
                                @click="addTaskToClipboard"
                            >
                                <v-icon left>
                                    mdi-plus
                                </v-icon>
                                Dodaj do schowka
                            </v-btn>
                            <v-btn
                                text
                                icon
                                color="primary"
                                class="hidden-md-and-up mx-1"
                                data-sentry-label="Otwórz czytnik kodów"
                                @click="showCodeReaderDialog({
                                    expectedQrTypeCodes: ['KZ', 'KZM', 'KP', 'LOC'],
                                    origin: 'listCamera'
                                })"
                            >
                                <v-icon>mdi-camera</v-icon>
                            </v-btn>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text v-if="tasksClipboard.length === 0">
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Schowek jest pusty.
                                    </v-alert>
                                </v-card-text>

                                <v-data-table
                                    v-else
                                    v-model="tasksSelectedToDrop"
                                    :headers="tasksClipboardHeaders"
                                    :items="tasksClipboard"
                                    item-key="taskId"
                                    show-select
                                    hide-default-footer
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    name="tasksClipboardData"
                                />

                                <v-divider />
                                <v-card-actions>
                                    <v-btn
                                        color="success"
                                        medium
                                        text
                                        data-sentry-label="Odłóż zaznaczone"
                                        :disabled="tasksSelectedToDrop.length == 0"
                                        @click="openLocalizationDialog"
                                    >
                                        Odłóż zaznaczone
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import CodeReaderDialog from '@/components/common/CodeReaderDialog'
import TaskDialog from './TaskDialog'
import LocalizationDialog from './LocalizationDialog'
import decodeQRcode from '@/mixins/decodeQRcode'
import { errorHandler } from '@/utils/errorHandler'
import { differenceBy } from 'lodash'

export default {
    name: 'Tracking',
    components: {
        PageTitle,
        CodeReaderDialog,
        TaskDialog,
        LocalizationDialog,
    },
    mixins: [ decodeQRcode ],
    data() {
        return {
            bufferLocalizationId: 1,
            tasksClipboard: [],
            tasksSelectedToDrop: [],
            tasksClipboardHeaders: [
                {
                    text: 'Numer zlecenia / zamówienia',
                    align: 'left',
                    sortable: false,
                    value: 'taskNumber'
                },
                {
                    text: 'Montaż',
                    align: 'left',
                    sortable: false,
                    value: 'plateNumber'
                },
                {
                    text: 'Ostatnia lokalizacja',
                    align: 'left',
                    sortable: false,
                    value: 'lastLocalization'
                }
            ],
            taskDialog: false,
            localizationDialog: false,
            actionLoading: false,
            decodedQr: null,
            availableQrTaskCodes: ['KZ', 'KZM', 'KP'],
            codeReaderSettings: {
                expectedQrTypeCodes: null,
                origin: null
            },
            codeReaderDialog: false,
        }
    },
    computed: {
        localizations() {
            return this.$store.state.shipments.manageTracking.manageTracking.localizations
        },
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
    },
    mounted() {
        this.$store.dispatch('shipments/manageTracking/getLocations')
            .catch(error => {
                errorHandler(error)
            })
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        addTaskToClipboard() {
            this.taskDialog = true
        },
        setTasksClipboard({taskId, taskNumber, task}) {
            const isItemInClipboard = this.tasksClipboard.find(item => item.taskId === taskId)

            if (typeof isItemInClipboard !== 'undefined') {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Element znajduje się już w schowku.'
                })
            } else {
                // Add task to buffer
                this.actionLoading = true
                this.$store.dispatch('shipments/manageTracking/setTasksLocalization', {
                    localizationWareNumbers: [taskId],
                    localizationId: this.bufferLocalizationId
                })
                    .then(() => {
                        this.tasksClipboard.push({
                            taskId: taskId,
                            taskNumber: taskNumber,
                            plateNumber: task.plateNumber || '---',
                            lastLocalization: task.lastLocalization || '---'
                        })

                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Dodano element do schowka.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading = false
                    })
            }
        },
        closeTaskDialog() {
            this.taskDialog = false
        },
        openLocalizationDialog() {
            this.localizationDialog = true
        },
        closeLocalizationDialog() {
            this.localizationDialog = false
        },
        removeFromClipboard(tasks) {
            this.tasksClipboard = differenceBy(this.tasksClipboard, tasks, 'taskId')
            this.tasksSelectedToDrop = differenceBy(this.tasksSelectedToDrop, tasks, 'taskId')
        },
        showCodeReaderDialog(data) {
            this.codeReaderSettings = data
            this.codeReaderDialog = true
        },
        hideCodeReaderDialog() {
            this.codeReaderDialog = false
        },
        handleQrJSON({ data, origin }) {
            switch (origin) {
                case 'scanner':
                case 'listCamera':
                    if (this.availableQrTaskCodes.includes(data.type)) {
                        if (!this.localizationDialog) {
                            this.taskDialog = true
                            this.decodedQr = data
                        }
                    } else if (data.type === 'LOC') {
                        if (this.tasksSelectedToDrop.length === 0) {
                            this.$store.dispatch('showNotification', {
                                type: 'error',
                                message: 'Nie wybrano zleceń lub zamówień do odłożenia.'
                            })

                            return
                        }

                        if (!this.taskDialog) {
                            this.localizationDialog = true
                            this.decodedQr = data
                        }
                    } else {
                        this.$store.dispatch('showNotification', {
                            type: 'error',
                            message: this.$i18n.t('reports.messages.incorrectQrCodeType')
                        })
                    }
                    break
                case 'dialogCamera':
                    this.decodedQr = data
                    break
                default:
                    break
            }
        }
    }
}
</script>
